import { type Media } from "@common/declarations";
import { Common } from "@common/types";

export namespace Users {
  export type User = Api.User & {
    addressLine: string | null;
    name: string;
    viewId?: string | null;
  };

  export namespace Api {
    export interface OtpStep {
      id: string;
      destination: string;
      messageType: OtpMessageType;
      message?: string;
      methods?: OtpStepMethod[];
    }

    export type OtpStepMethod = "Sms" | "Voice";

    export type OtpMessageType = "Email" | "Text" | "WhatsApp";

    export type UnsubscribeReasonKey = "Inappropriate" | "Spam" | "Staff" | "Unsolicited" | "Unsubscribe";

    export interface UserAddressBase {
      userId: number;
      firstName: string | null;
      lastName: string | null;
      company?: string | null;
      companyTaxNumber?: string | null;
      countryCode: string | null;
      subdivisionCode: string | null;
      city: string | null;
      postalCode: string | null;
      street: string | null;
      unit: string | null;
      email: string;
      telephone: string | null;
      latitude?: number | null;
      longitude?: number | null;
    }

    export interface User {
      acceptedHelpdeskGuidelinesOnDate: string | null;
      affiliateDate: string | null;
      biography: string | null;
      city: string | null;
      company: string | null;
      companyTaxNumber: string | null;
      createdByUserId: number;
      createdOnDate: string;
      cultureCode: string | null;
      email: string;
      emailProcessedStatusKey: number | null;
      emailValidationStatusAllowMarketing: boolean;
      emailValidationStatusAllowTransactional: boolean;
      emailValidationStatusDescriptionLabel: string | null;
      emailValidationStatusKey: number | null;
      engagementRating: number;
      firstName: string | null;
      hasAcceptedMarketing: boolean | null;
      hasAcceptedMarketingLastModifiedOnDate: string | null;
      hasAcceptedHelpdeskGuidelines: boolean | null;
      isBouncing: boolean | null;
      isDeleted: boolean;
      countryCode: string | null;
      currencyCode: string;
      subdivisionCode: string | null;
      isStaff: boolean;
      isUnsubscribed: boolean;
      lastIPAddress: string | null;
      lastLoginDate: string | null;
      lastModifiedByUserId: number;
      lastModifiedOnDate: string | null;
      lastName: string | null;
      parentUserId: number | null;
      pinRankColor: string | null;
      pinRankName: string | null;
      postalCode: string | null;
      profilePictureMediaId: string | null;
      profilePictureMediaSourceId: string | null;
      profilePictureMediaSourceKey: Common.Media.Source | null;
      rankColor: string | null;
      rankDemotionGraceDate: string | null;
      rankId: string | null;
      rankName: string | null;
      statusDemotionGraceDate: string | null;
      statusId: string | null;
      statusName: string | null;
      street: string | null;
      supplierCount: number;
      telephone: string | null;
      timeZoneKey: string | null;
      unit: string | null;
      unsubscribeReasonKey: UnsubscribeReasonKey | null;
      userGuid: string;
      userId: number;
      warehouseCount: number;
      website: string | null;

      accountBalance: number | null;
    }

    export type UserStatus = "Hyperactive" | "Inactive" | "Active";

    export interface DashboardAffiliate {
      accountBalance: number;
      affiliateDate: string;
      firstName: string | null;
      currencyCode: string;
      lastName: string | null;
      pinRankColor: string | null;
      pinRankName: string | null;
      profilePictureMediaSourceId: string | null;
      profilePictureMediaSourceKey: Media.Source | null;
      profilePictureMediaUrl: string | null;
      rankColor: string | null;
      rankId: string | null;
      rankName: string | null;
      statusName: UserStatus | null;
      userId: number;
    }

    export type GetNotificationsRequest = Omit<Common.Api.PageRequest, "searchText" | "order"> & {
      notificationTypeKey?: NotificationType | null;
    };

    export type NotificationCount = {
      notificationTypeKey: NotificationType;
      readNotificationCount: number;
      unreadNotificationCount: number;
      maxCreatedOnDate: string;
    };

    export type Notification = {
      notificationId: string;
      notificationTypeLabel: string;
      userId: number;
      notificationTypeKey: NotificationType;
      isRead: boolean;
      postId: string | null;
      postTitle: string | null;
      postCommentId: string | null;
      formId: string | null;
      formUserId: number | null;
      historyId: string | null;
      orderId: string | null;
      productReviewId: string | null;
      formName: string | null;
      orderNumber: number | null;
      productId: string | null;
      ticketId: string | null;
      productName: string | null;
      ticketNumber: number | null;

      triggerUserId: number | null;
      triggerUserFirstName: string | null;
      triggerUserLastName: string | null;

      createdByUserId: number;
      createdOnDate: string;
      lastModifiedByUserId: number | null;
      lastModifiedOnDate: string | null;
    };

    export interface UserNotificationType {
      createdByUserId?: number | null;
      createdOnDate?: string;
      emailIsEnabled: boolean;
      formCount?: number;
      ids: string[] | null;
      lastModifiedByUserId?: number | null;
      lastModifiedOnDate?: Date;
      notificationTypeKey: NotificationType;
      notificationTypeLabel?: string;
      postCount?: number;
      productCount?: number;
      smsIsEnabled: boolean;
    }

    export enum NotificationType {
      BlogPostComments = "BlogPostComments",
      FormSubmissions = "FormSubmissions",
      Orders = "Orders",
      OrderComments = "OrderComments",
      ProductReviews = "ProductReviews",
      Tickets = "Tickets",
      TicketComments = "TicketComments"
    }
  }

  export namespace View {
    export interface NotificationType {
      type: Api.NotificationType;
      isHidden: boolean;
    }
  }
}
